import type { Observable } from 'rxjs';
import type { Uuid } from '@common/types';

import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import {
    ONBOARDING_WIZARD_EXPERIMENT,
    ONBOARDING_WIZARD_QUERY_PARAM,
} from '@common/constants/onboarding-wizard-ab-test.const';

import { SiteFacade } from '@web/store/website/site/site.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { AppFacade } from '@web/store/app/app.facade';
import SiteActions from '@web/store/website/site/site.actions';
import PagesActions from '@web/store/website/pages/pages.actions';

export const ONBOARDING_WIZARD_SITES_STORAGE_KEY = 'sp-web-onboarding-wizard-ab-test';
export const ONBOARDING_WIZARD_PENDING_ENTRY_KEY = 'sp-web-onboarding-wizard-pending';

type OnboardingWizardSiteRecord = {
    siteId: string;
    userId: number;
    isPublished: boolean;
};

/**
 * "Create site onboarding" AB-test logic.
 *
 * Two concerns are intentionally decoupled:
 *  1. Storage record — tracks that the constructor was opened with the `isOnboardingWizard=1`
 *     param, then (after the first save) which site/user it belongs to, then (after the first
 *     publish) that the test is completed. This does NOT depend on the experiment flag.
 *  2. Experiment flag — the GTM `dataLayer` group check, evaluated only at display time.
 *
 * The UI shows only when BOTH hold: the site/user is an active record AND the experiment is
 * enabled for the user — so a stored record with the experiment disabled shows nothing.
 *
 * Storage is split on purpose:
 *  - `pendingEntry` → sessionStorage: "this tab opened the constructor with the param", awaiting
 *    the first save. Per-tab and dropped when the tab closes, so an abandoned entry never leaks to
 *    another tab/session and can never bind an unrelated site.
 *  - `sites` → localStorage: bound sites, persisted across sessions for display and to remember
 *    which sites already left the test after publishing.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingWizardAbTestService {
    private readonly actions$ = inject(Actions);
    private readonly siteFacade = inject(SiteFacade);
    private readonly userFacade = inject(UserFacade);
    private readonly appFacade = inject(AppFacade);

    private readonly sites$ = new BehaviorSubject<OnboardingWizardSiteRecord[]>(this.readSites());
    // Per-tab "entered with the onboarding param, not yet bound to a saved site" flag.
    private readonly pendingEntry$ = new BehaviorSubject<boolean>(this.readPendingEntry());

    /**
     * Emits `true` when the experiment is enabled AND the user is in the onboarding flow — either
     * still pending (entered with the param, before the first save when there is no real siteId yet)
     * OR already bound to this non-published site/user. So the steps show from entry, through the
     * first save, until the first publish.
     */
    public readonly showOnboardingSteps$: Observable<boolean> = combineLatest([
        this.sites$,
        this.pendingEntry$,
        this.siteFacade.id$,
        this.userFacade.userId$,
        this.appFacade.dataLayer$,
    ]).pipe(
        map(
            ([sites, pendingEntry, siteId, userId, dataLayer]) =>
                this.isInTestGroup(dataLayer) && (pendingEntry || this.isActiveParticipant(sites, siteId, userId))
        )
    );

    /**
     * Records (per tab) that the constructor was opened with the onboarding param. Independent of
     * the experiment flag. Must run before the first in-app navigation drops the query param
     * (`navigateSitePage` uses `replaceUrl` without `queryParamsHandling`).
     */
    public captureEntryParams(): void {
        const params = new URLSearchParams(window.location.search);

        if (params.get(ONBOARDING_WIZARD_QUERY_PARAM) === '1') {
            this.setPendingEntry(true);
        }
    }

    /** Binds the site on its first save (real id) and leaves the test on its first publish. */
    public initTracking(): void {
        // Bind the site only when a brand-new site is created (the wizard always creates one). Using
        // `createSiteSuccess` — not the appearance of a real siteId — means opening or updating an
        // existing site never binds it, even if a stale pending flag is still set.
        this.actions$
            .pipe(ofType(SiteActions.createSiteSuccess), withLatestFrom(this.userFacade.userId$))
            .subscribe(([{ site }, userId]) => this.bindSiteIfPending(site.id, userId));

        // Leave the test after the site is published.
        this.actions$
            .pipe(
                ofType(PagesActions.activatePageSuccess),
                withLatestFrom(this.siteFacade.id$, this.userFacade.userId$)
            )
            .subscribe(([, siteId, userId]) => this.markSitePublished(siteId, userId));
    }

    private isInTestGroup(dataLayer: any): boolean {
        // The experiment flag comes from the store data layer (/members/ajax/data-layer), NOT the
        // global GTM array (which is defined but empty inside the constructor). Handle both an
        // object ({ exp }) and an array ([{ exp }]) shape; `exp` may be a string or an array.
        const entry = Array.isArray(dataLayer) ? dataLayer[0] : dataLayer;
        const exp = entry?.exp;

        return !!exp && exp.includes(ONBOARDING_WIZARD_EXPERIMENT);
    }

    private isActiveParticipant(sites: OnboardingWizardSiteRecord[], siteId: number | Uuid, userId: number): boolean {
        if (!siteId || !userId) {
            return false;
        }

        return sites.some(
            (record) => record.siteId === String(siteId) && record.userId === userId && !record.isPublished
        );
    }

    private bindSiteIfPending(siteId: number | Uuid, userId: number): void {
        if (!this.pendingEntry$.value) {
            return;
        }

        // The pending intent is fulfilled by the first real id; consume it for this tab.
        this.setPendingEntry(false);

        const sites = this.sites$.value;
        const alreadyBound = sites.some((record) => record.siteId === String(siteId) && record.userId === userId);

        if (alreadyBound) {
            return;
        }

        this.writeSites([...sites, { siteId: String(siteId), userId, isPublished: false }]);
    }

    private markSitePublished(siteId: number | Uuid, userId: number): void {
        const sites = this.sites$.value;
        const hasActiveRecord = sites.some(
            (record) => record.siteId === String(siteId) && record.userId === userId && !record.isPublished
        );

        if (!hasActiveRecord) {
            return;
        }

        this.writeSites(
            sites.map((record) =>
                record.siteId === String(siteId) && record.userId === userId ? { ...record, isPublished: true } : record
            )
        );
    }

    private setPendingEntry(value: boolean): void {
        try {
            if (value) {
                sessionStorage.setItem(ONBOARDING_WIZARD_PENDING_ENTRY_KEY, '1');
            } else {
                sessionStorage.removeItem(ONBOARDING_WIZARD_PENDING_ENTRY_KEY);
            }
        } catch {
            // Ignore storage errors (private mode / quota).
        }

        this.pendingEntry$.next(value);
    }

    private readPendingEntry(): boolean {
        try {
            return sessionStorage.getItem(ONBOARDING_WIZARD_PENDING_ENTRY_KEY) === '1';
        } catch {
            return false;
        }
    }

    private writeSites(sites: OnboardingWizardSiteRecord[]): void {
        try {
            localStorage.setItem(ONBOARDING_WIZARD_SITES_STORAGE_KEY, JSON.stringify(sites));
        } catch {
            // Ignore storage errors — in-memory state still drives the UI.
        }

        this.sites$.next(sites);
    }

    private readSites(): OnboardingWizardSiteRecord[] {
        try {
            const raw = localStorage.getItem(ONBOARDING_WIZARD_SITES_STORAGE_KEY);

            if (!raw) {
                return [];
            }

            const parsed: any = JSON.parse(raw);

            if (Array.isArray(parsed)) {
                return parsed as OnboardingWizardSiteRecord[];
            }

            // Tolerate the earlier `{ pendingEntry, sites }` shape.
            return Array.isArray(parsed?.sites) ? (parsed.sites as OnboardingWizardSiteRecord[]) : [];
        } catch {
            return [];
        }
    }
}
